import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'
import lottie from 'lottie-web'
import ButtonVideo from 'template-parts/buttons/button-video'
import { postAjaxCleanup } from 'assets/js/utils/helpers'

const SELECTORS = {
  allStories: '.js-all-stories',
  toggleMenuButton: '.js-all-stories__button',
  allStoriesToggle: '.js-all-stories__all-stories-toggle',
  filterMenu: '.js-all-stories__list',
  loadingContainer: '.js-all-stories__load_container',
  loadButton: '.js-all-stories__load_btn',
  loadButtonText: '.js-all-stories__load_btn__text',
  filterPill: 'input[type="radio"]',
  wrapper: '.js-all-stories__wrapper',
  items: ':scope > :not(.all-stories__wrapper__empty)',
  openCaret: '.js-all-stories__open',
  closeCaret: '.js-all-stories__close',
  card: '.js-story-card',
  videoCard: '.js-media-button',
  cardLink: '.js-story-card-link',
  storiesListTitle: '.js-all-stories-list-title'
}

const CLASSES = {
  hide: '-hide',
  loadingState: 'all-stories__loading-state',
  loadingSpinner: 'all-stories__loading-spinner'
}

class AllStories {
  constructor(element) {
    this.element = element
    this.loadingContainer = this.element.querySelector(SELECTORS.loadingContainer)
    this.loadBtn = this.element.querySelector(SELECTORS.loadButton)
    this.loadBtnText = this.element.querySelector(SELECTORS.loadButtonText)
    this.toggleMenuButton = this.element.querySelector(SELECTORS.toggleMenuButton)
    this.allStoriesToggle = this.element.querySelector(SELECTORS.allStoriesToggle)
    this.filterMenu = this.element.querySelector(SELECTORS.filterMenu)
    this.filterPill = this.filterMenu.querySelectorAll(SELECTORS.filterPill)
    this.wrapper = this.element.querySelector(SELECTORS.wrapper)
    this.openCaret = this.element.querySelector(SELECTORS.openCaret)
    this.closeCaret = this.element.querySelector(SELECTORS.closeCaret)
    this.storiesListTitle = this.element.querySelector(SELECTORS.storiesListTitle)
    this.init()
  }

  init() {
    this.registerEvents()

    this.storiesListTitle.innerHTML = this.getStoriesListTitle(
      this.storiesListTitle.dataset.collection,
      this.storiesListTitle.dataset.storyCount,
      this.storiesListTitle.dataset.totalStories
    )
  }

  getLoadedItems() {
    return this.wrapper.querySelectorAll(SELECTORS.items)
  }

  getCategory() {
    const selectedItem = this.filterMenu.querySelector('.all-stories__radio:checked')
    return [selectedItem.value]
  }

  getCategoryName() {
    const selectedItem = this.filterMenu.querySelector('.all-stories__radio:checked')
    return selectedItem.labels[0].innerText
  }

  registerEvents() {
    this.loadBtn.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, (e) => this.loadPosts(e, 'load'))
    this.toggleMenuButton.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, (e) => this.toggleFilterMenu(e, 'load'))

    this.filterPill.forEach(item => {
      item.addEventListener('change', (e) => this.handleFilter(e))
    })
  }

  wpAjaxFetch(url, data) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      body: data
    })
      .then((response) => response.json())
      .catch((error) => { console.log('error', error) })
  }

  handleFilter(e) {
    this.loadPosts(e, 'filter')
  }

  toggleLoadBtn(type) {
    if (type === 'hide') {
      this.loadBtn.classList.add(CLASSES.hide)
    } else {
      this.loadBtn.classList.remove(CLASSES.hide)
    }
  }

  loadAnimation() {
    const animation = lottie.loadAnimation({
      container: document.querySelector(`.${CLASSES.loadingSpinner}`),
      path: `${unlocked.theme_dir}/assets/lottie/LoadingSpinner.json`,
      renderer: 'svg',
      loop: true,
      autoplay: true
    })

    if (!animation.isLoaded) {
      animation.addEventListener('DOMLoaded', () => animation.play)
    }

    return animation
  }

  addLoadingState() {
    const loadingState = `
      <span class="${CLASSES.loadingState}">
        <span class="all-stories__loading-state-text">Loading </span>
        <span class="${CLASSES.loadingSpinner}"></span>
      </span>`
    this.loadingContainer.innerHTML = loadingState
    this.loadAnimation()
    this.loadBtn.remove()
  }

  removeLoadingState() {
    const loadMoreButton = this.loadBtn
    this.loadingContainer.append(loadMoreButton)
    const loadingState = document.querySelector(`.${CLASSES.loadingState}`)
    loadingState?.remove()
  }

  setData() {
    const categories = this.getCategory()
    const allStoriesSelected = categories.includes('all-stories')

    if (allStoriesSelected) {
      this.filterPill.forEach(filter => {
        categories.push(filter.value)
      })
      return { string: 'categories', object: categories.join(',') }
    }

    if (categories.length > 0) {
      return { string: 'categories', object: categories.join(',') }
    }
  }

  loadPosts(e, type) {
    e.preventDefault()

    this.addLoadingState()

    const offset = type === 'load' ? this.getLoadedItems().length : 0
    const data = new FormData()
    data.append('nonce', this.element.dataset.nonce)
    data.append('action', 'unlocked_load_stories')
    data.append('offset', offset)
    const { string, object } = this.setData()
    data.append(string, object)

    // fetch the stories
    this.wpAjaxFetch(unlocked.ajax_url, data).then((res) => {
      let { content, postsRemaining, totalPosts } = res

      if (content) {
        content = postAjaxCleanup(content)
        if (type === 'load') {
          let allCards = this.wrapper.querySelectorAll(SELECTORS.card)
          const { length } = allCards
          this.wrapper.insertAdjacentHTML('beforeend', content)
          allCards = this.wrapper.querySelectorAll(SELECTORS.card)
          const firstNewCard = allCards[length]
          firstNewCard.querySelector(SELECTORS.cardLink).focus()
        } else {
          this.wrapper.innerHTML = content
        }
      }

      this.storiesListTitle.innerHTML = this.getStoriesListTitle(this.getCategoryName(), totalPosts - postsRemaining, totalPosts)
      this.removeLoadingState()

      if (postsRemaining > 0) {
        this.toggleLoadBtn('show')
      } else {
        this.toggleLoadBtn('hide')
      }

      // update the cards array with the current rendered cards
      this.videoCards = this.element.querySelectorAll(SELECTORS.videoCard)

      // instantiate each card so that the displayMedia method runs
      this.videoCards.forEach(card => {
        new ButtonVideo.Source(card)
      })
    })
  }

  toggleFilterMenu() {
    const buttonOpen = this.toggleMenuButton.getAttribute('aria-expanded')
    buttonOpen === 'false'
      ? this.toggleMenuButton.setAttribute('aria-expanded', 'true')
      : this.toggleMenuButton.setAttribute('aria-expanded', 'false')

    // toggle SVG carets
    if (buttonOpen === 'false') {
      this.openCaret.classList.add(CLASSES.hide)
      this.closeCaret.classList.remove(CLASSES.hide)
      this.allStoriesToggle.focus()
    } else if (buttonOpen === 'true') {
      this.openCaret.classList.remove(CLASSES.hide)
      this.closeCaret.classList.add(CLASSES.hide)
    }
  }

  getStoriesListTitle(collection, currentCount, totalCount) {
    const start = (collection === 'All Collections')
      ? 'Stories from all collections'
      : `Stories from the collection ${collection}`

    const end = (currentCount === totalCount)
      ? `results ${totalCount}`
      : `${currentCount} of ${totalCount}`

    return `${start}: ${end}`
  }
}

export default {
  selector: SELECTORS.allStories,
  Source: AllStories
}
